/**
 * OmegaEnhancementsCard — iter 137
 *
 * Renders the live, account-aware enhancement ideas generated by Claude
 * Sonnet 4.6 from the real `/api/omega/enhancement-ideas` endpoint.
 *
 * No mocks, no placeholders. If the API returns no ideas (LLM offline,
 * snapshot too thin) the card shows an honest empty state with a refresh
 * button — never fake data.
 *
 * Variants:
 *   - <OmegaEnhancementsCard variant="full" dispatch={fn} />     (Analytics tab)
 *   - <OmegaEnhancementsCard variant="preview" dispatch={fn} />  (Account tab top-idea card)
 *
 * `dispatch(action, payload)` is implemented by App.js and routes the
 * whitelisted backend actions to real UI operations:
 *   - open_risk_dashboard / open_analytics / open_intel / open_settings / open_omega_tea / open_options_hub
 *   - open_ticker        → opens the ticker view for payload.symbol
 *   - open_recovery_plan → switches to risk tab and surfaces recovery
 *   - run_auto_tune      → posts /api/hedge/auto-tune
 *   - adjust_zi_threshold → posts /api/user/state with new threshold
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRIORITY_TONE = {
  P0: { bg: "rgba(248,113,113,0.10)", border: "rgba(248,113,113,0.42)", color: "#fca5a5", label: "P0 · ACT TODAY" },
  P1: { bg: "rgba(251,191,36,0.10)", border: "rgba(251,191,36,0.42)", color: "#fcd34d", label: "P1 · THIS WEEK" },
  P2: { bg: "rgba(96,165,250,0.10)", border: "rgba(96,165,250,0.42)", color: "#93c5fd", label: "P2 · NICE-TO-HAVE" },
};

const CATEGORY_ICON = {
  Risk: "⚠",
  Allocation: "◐",
  Strategy: "✦",
  Execution: "▶",
  Education: "✎",
};

function fmtRelTime(iso) {
  if (!iso) return "—";
  try {
    const diff = Math.max(0, Date.now() - new Date(iso).getTime());
    const s = Math.floor(diff / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m} min ago`;
    const h = Math.floor(m / 60);
    return `${h}h ago`;
  } catch { return "—"; }
}

export default function OmegaEnhancementsCard({ variant = "full", dispatch }) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [actingOn, setActingOn] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/omega/enhancement-ideas`, { timeout: 35000 });
      setPayload(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed to load enhancement ideas");
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      const r = await axios.post(`${API}/omega/enhancement-ideas/refresh`, {}, { timeout: 35000 });
      setPayload(r.data);
      toast.success("Omega refreshed enhancement ideas", { duration: 2500 });
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Auto-refresh every 5 minutes (cache is 10 min on backend so this is cheap)
    const id = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [load]);

  const handleAction = useCallback(async (idea) => {
    if (!idea || !dispatch || idea.action === "none") return;
    setActingOn(idea.id);
    try {
      await dispatch(idea.action, idea.actionPayload, idea);
    } finally {
      setActingOn(null);
    }
  }, [dispatch]);

  const ideas = payload?.ideas || [];
  const summary = payload?.snapshotSummary || null;
  const isPreview = variant === "preview";
  const top = ideas[0];

  // ───── PREVIEW variant — compact card for Account tab ─────
  if (isPreview) {
    if (loading && !top) {
      return (
        <div className="card" data-testid="enhance-preview" style={{ borderColor: "rgba(167,139,250,0.25)" }}>
          <div className="sh" style={{ margin: 0 }}>Omega Enhancement · Top idea</div>
          <div style={{ fontSize: 12, color: "var(--dim)", marginTop: 8 }}>Reading your live state…</div>
        </div>
      );
    }
    if (error || !top) {
      return (
        <div className="card" data-testid="enhance-preview" style={{ borderColor: "rgba(167,139,250,0.25)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
            <div className="sh" style={{ margin: 0 }}>Omega Enhancement · Top idea</div>
            <button onClick={refresh} disabled={refreshing} data-testid="enhance-preview-refresh" style={refreshBtnStyle(refreshing)}>
              {refreshing ? "Generating…" : "Generate"}
            </button>
          </div>
          <div style={{ fontSize: 12, color: "var(--dim)", marginTop: 8 }}>
            {error ? `Couldn't reach the recommendation engine: ${String(error).slice(0, 120)}` :
             "No live enhancement ideas yet. Tap Generate to have Omega review your account."}
          </div>
        </div>
      );
    }
    const tone = PRIORITY_TONE[top.priority] || PRIORITY_TONE.P2;
    return (
      <div className="card" data-testid="enhance-preview" style={{ borderColor: "rgba(167,139,250,0.3)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <div className="sh" style={{ margin: 0 }}>Omega Enhancement · Top idea</div>
          <a href="#analytics-enhance" onClick={(e) => { e.preventDefault(); dispatch && dispatch("open_analytics", { sub: "enhance" }); }}
             style={{ fontSize: 10, color: "var(--blue)", textDecoration: "none" }} data-testid="enhance-preview-viewall">
            View all {ideas.length} →
          </a>
        </div>
        <div style={{
          padding: 12, borderRadius: 10,
          background: tone.bg, border: `1px solid ${tone.border}`,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
            <span style={priorityPillStyle(tone)}>{tone.label}</span>
            <span style={categoryPillStyle()}>{CATEGORY_ICON[top.category] || "•"} {top.category.toUpperCase()}</span>
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", lineHeight: 1.35, marginBottom: 6 }}>{top.title}</div>
          <div style={{ fontSize: 11.5, color: "var(--dim)", lineHeight: 1.55, marginBottom: 10 }}>{top.rationale}</div>
          {top.action !== "none" && (
            <button
              data-testid={`enhance-preview-action`}
              onClick={() => handleAction(top)}
              disabled={actingOn === top.id}
              style={actionBtnStyle(tone, actingOn === top.id)}
            >
              {actingOn === top.id ? "Working…" : top.actionLabel}
              <span style={{ marginLeft: 6 }}>→</span>
            </button>
          )}
        </div>
      </div>
    );
  }

  // ───── FULL variant — Analytics → Enhance sub-tab ─────
  return (
    <div data-testid="enhance-full">
      <div className="card" style={{ borderColor: "rgba(167,139,250,0.28)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
          <div>
            <div className="sh" style={{ margin: 0 }}>Omega Enhancement Ideas</div>
            <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4, lineHeight: 1.5 }}>
              Live AI recommendations generated by <strong style={{ color: "#a78bfa" }}>Claude Sonnet 4.6</strong> from your actual portfolio,
              brain weights, attribution, news, and recovery state. Every rationale cites real numbers — no generic advice.
            </div>
          </div>
          <button
            data-testid="enhance-refresh"
            onClick={refresh}
            disabled={refreshing || loading}
            style={refreshBtnStyle(refreshing || loading)}
          >
            {refreshing ? "Refreshing…" : "↻ Refresh"}
          </button>
        </div>

        {summary && (
          <div style={{
            marginTop: 12, padding: 10, borderRadius: 10,
            background: "rgba(255,255,255,0.025)", border: "0.5px solid rgba(255,255,255,0.06)",
            display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
            fontSize: 11,
          }} data-testid="enhance-snapshot">
            <SnapStat label="Open positions" value={summary.openPositions ?? "—"} />
            <SnapStat label="Total equity" value={summary.totalEquity != null ? `$${Number(summary.totalEquity).toFixed(2)}` : "—"} />
            <SnapStat label="Trades analyzed" value={summary.attributionTrades ?? "—"} />
            <SnapStat label="Brain win rate" value={summary.attributionWinRate != null ? `${summary.attributionWinRate}%` : "—"} />
            <SnapStat label="News events" value={summary.newsConsidered ?? 0} />
            <SnapStat label="Recovery plan" value={summary.hasRecoveryPlan ? "Active" : "None"} />
          </div>
        )}

        {payload?.generatedAt && (
          <div style={{ fontSize: 10, color: "var(--dim)", marginTop: 10 }}>
            Generated {fmtRelTime(payload.generatedAt)}
            {payload.fromCache ? " · served from cache (10-min TTL)" : " · fresh from Claude"}
            {payload.model ? ` · ${payload.model}` : ""}
          </div>
        )}
      </div>

      {loading && !ideas.length && (
        <div className="card" style={{ marginTop: 12, textAlign: "center", color: "var(--dim)", fontSize: 12 }}>
          Omega is reviewing your live account state…
        </div>
      )}

      {error && (
        <div className="card" style={{ marginTop: 12, borderColor: "rgba(248,113,113,0.35)", background: "rgba(248,113,113,0.06)" }}>
          <div style={{ fontSize: 12, color: "#fca5a5", fontWeight: 600 }}>Couldn't reach the recommendation engine</div>
          <div style={{ fontSize: 11, color: "var(--dim)", marginTop: 4 }}>{String(error).slice(0, 240)}</div>
        </div>
      )}

      {!loading && !error && ideas.length === 0 && (
        <div className="card" style={{ marginTop: 12, textAlign: "center" }}>
          <div style={{ fontSize: 12, color: "var(--text)", fontWeight: 600 }}>No enhancement ideas right now</div>
          <div style={{ fontSize: 11, color: "var(--dim)", marginTop: 4, lineHeight: 1.5 }}>
            The LLM didn't return valid ideas on this snapshot. This usually means your account state is
            very thin (no positions, no closed trades, no recent news). Trade a few setups, then refresh.
          </div>
        </div>
      )}

      <div style={{ display: "grid", gap: 10, marginTop: 12 }} data-testid="enhance-list">
        {ideas.map((idea, idx) => {
          const tone = PRIORITY_TONE[idea.priority] || PRIORITY_TONE.P2;
          return (
            <div
              key={idea.id || idx}
              data-testid={`enhance-idea-${idx}`}
              className="card"
              style={{
                borderColor: tone.border,
                background: `linear-gradient(155deg, ${tone.bg} 0%, var(--layer1, rgba(255,255,255,0.03)) 90%)`,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                <span style={priorityPillStyle(tone)}>{tone.label}</span>
                <span style={categoryPillStyle()}>{CATEGORY_ICON[idea.category] || "•"} {idea.category.toUpperCase()}</span>
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)", lineHeight: 1.35, marginBottom: 8 }}>
                {idea.title}
              </div>
              <div style={{ fontSize: 12, color: "var(--dim)", lineHeight: 1.6, marginBottom: 10 }}>
                {idea.rationale}
              </div>
              {idea.expectedImpact && (
                <div style={{
                  fontSize: 11, color: tone.color, marginBottom: 10,
                  padding: "6px 10px", borderRadius: 8,
                  background: "rgba(255,255,255,0.02)",
                  border: `1px solid ${tone.border}`,
                  fontStyle: "italic",
                }}>
                  Expected impact: {idea.expectedImpact}
                </div>
              )}
              {idea.action !== "none" && (
                <button
                  data-testid={`enhance-action-${idx}`}
                  onClick={() => handleAction(idea)}
                  disabled={actingOn === idea.id}
                  style={actionBtnStyle(tone, actingOn === idea.id)}
                >
                  {actingOn === idea.id ? "Working…" : idea.actionLabel}
                  <span style={{ marginLeft: 6 }}>→</span>
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* iter 138: Trade Retrospective panel — real closed-loss post-mortems */}
      <RecentLossesPanel />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RecentLossesPanel — lists the user's last 5 real closed losing autopilot
// trades and lets them request a Claude-powered post-mortem per trade.
// No mock data; if the user has no losses the panel says so honestly.
// ─────────────────────────────────────────────────────────────────────────────
function RecentLossesPanel() {
  const [losses, setLosses] = useState(null); // null = loading, [] = no losses
  const [error, setError] = useState(null);
  const [analyzingId, setAnalyzingId] = useState(null);
  const [retros, setRetros] = useState({}); // tradeId → { retrospective, fromCache, generatedAt }

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/omega/recent-losses?limit=5`)
      .then(r => { if (!cancelled) setLosses(r.data?.losses || []); })
      .catch(e => { if (!cancelled) setError(e.response?.data?.detail || e.message); })
      .finally(() => {});
    return () => { cancelled = true; };
  }, []);

  const analyze = async (tradeId) => {
    setAnalyzingId(tradeId);
    try {
      const r = await axios.post(`${API}/omega/trade-retrospective/${encodeURIComponent(tradeId)}`, {}, { timeout: 35000 });
      setRetros(prev => ({ ...prev, [tradeId]: r.data }));
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Analysis failed";
      toast.error(`Couldn't analyze trade: ${String(detail).slice(0, 140)}`);
    } finally {
      setAnalyzingId(null);
    }
  };

  return (
    <div className="card" data-testid="recent-losses-panel" style={{ marginTop: 14, borderColor: "rgba(248,113,113,0.22)" }}>
      <div className="sh" style={{ margin: 0 }}>Recent losses · learn the miss</div>
      <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4, marginBottom: 10, lineHeight: 1.5 }}>
        Real closed losing trades from <code>db.autopilot_positions</code>. Click <strong>Analyze</strong> on any row to have
        Claude review the actual entry vector signature + realized outcome and propose a concrete brain guardrail.
      </div>

      {losses === null && !error && (
        <div style={{ fontSize: 11, color: "var(--dim)", padding: 6 }}>Loading recent losses…</div>
      )}

      {error && (
        <div style={{ fontSize: 11, color: "#fca5a5", padding: 8, borderRadius: 8, background: "rgba(248,113,113,0.06)", border: "1px solid rgba(248,113,113,0.25)" }}>
          Couldn't load recent losses: {String(error).slice(0, 200)}
        </div>
      )}

      {losses && losses.length === 0 && !error && (
        <div style={{ fontSize: 11.5, color: "var(--dim)", padding: 10, borderRadius: 8, background: "rgba(74,222,128,0.04)", border: "1px solid rgba(74,222,128,0.18)" }} data-testid="recent-losses-empty">
          No closed losing trades yet. Once Omega closes a losing position, you'll be able to ask for a per-trade post-mortem here.
        </div>
      )}

      {losses && losses.length > 0 && (
        <div style={{ display: "grid", gap: 8 }}>
          {losses.map((t) => {
            const retro = retros[t.id];
            return (
              <div key={t.id} data-testid={`loss-row-${t.id}`} style={{
                padding: 10, borderRadius: 10,
                background: "rgba(248,113,113,0.04)",
                border: "1px solid rgba(248,113,113,0.18)",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>
                      {t.symbol || "—"}
                      {t.ziClass && <span style={{ marginLeft: 8, fontSize: 9, padding: "2px 6px", borderRadius: 4, background: "rgba(255,255,255,0.05)", color: "var(--muted)", letterSpacing: 0.6 }}>{t.ziClass}</span>}
                    </div>
                    <div style={{ fontSize: 10.5, color: "var(--dim)", marginTop: 3 }}>
                      Closed {fmtRelTime(t.closedAt)}
                      {t.pnl != null && <span style={{ marginLeft: 8, color: "#fca5a5", fontWeight: 600 }}>{t.pnl < 0 ? "-$" : "$"}{Math.abs(t.pnl).toFixed(2)}</span>}
                      {t.pnlPct != null && <span style={{ marginLeft: 4, color: "#fca5a5", fontWeight: 500 }}>({t.pnlPct < 0 ? "" : "+"}{t.pnlPct.toFixed(1)}%)</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => analyze(t.id)}
                    disabled={analyzingId === t.id || !!retro}
                    data-testid={`analyze-loss-${t.id}`}
                    style={{
                      fontSize: 11, fontWeight: 700,
                      padding: "6px 12px", borderRadius: 8,
                      background: retro ? "rgba(74,222,128,0.08)" : (analyzingId === t.id ? "rgba(248,113,113,0.06)" : "rgba(248,113,113,0.14)"),
                      border: `1px solid ${retro ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.42)"}`,
                      color: retro ? "#4ade80" : "#fca5a5",
                      cursor: (analyzingId === t.id || !!retro) ? "default" : "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    {retro ? "✓ Analyzed" : analyzingId === t.id ? "Analyzing…" : "Analyze loss"}
                  </button>
                </div>
                {retro?.retrospective && (
                  <div style={{
                    marginTop: 10, padding: 10, borderRadius: 8,
                    background: "rgba(15,23,42,0.45)",
                    border: "1px solid rgba(255,255,255,0.06)",
                    fontSize: 12, lineHeight: 1.6, color: "var(--text)",
                    whiteSpace: "pre-wrap",
                  }} data-testid={`retro-text-${t.id}`}>
                    {retro.retrospective}
                    <div style={{ marginTop: 6, fontSize: 9, color: "var(--dim)" }}>
                      {retro.fromCache ? "served from cache" : "fresh from Claude"} · {retro.model || "claude-sonnet-4-6"}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SnapStat({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 9, color: "var(--dim)", textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, color: "var(--text)", fontWeight: 700 }}>{value}</div>
    </div>
  );
}

function priorityPillStyle(tone) {
  return {
    fontSize: 9, fontWeight: 800, letterSpacing: 1.2,
    padding: "3px 8px", borderRadius: 5,
    background: tone.bg, color: tone.color,
    border: `1px solid ${tone.border}`,
  };
}

function categoryPillStyle() {
  return {
    fontSize: 9, fontWeight: 700, letterSpacing: 1,
    padding: "3px 8px", borderRadius: 5,
    background: "rgba(255,255,255,0.04)",
    color: "var(--muted)",
    border: "0.5px solid rgba(255,255,255,0.08)",
  };
}

function actionBtnStyle(tone, working) {
  return {
    fontSize: 12, fontWeight: 700,
    padding: "8px 14px", borderRadius: 10,
    background: working ? "rgba(255,255,255,0.06)" : tone.bg,
    border: `1px solid ${tone.border}`,
    color: tone.color, cursor: working ? "default" : "pointer",
    transition: "transform 120ms ease, background 160ms ease",
    fontFamily: "inherit",
  };
}

function refreshBtnStyle(working) {
  return {
    fontSize: 11, fontWeight: 700, padding: "6px 12px", borderRadius: 8,
    background: working ? "rgba(167,139,250,0.06)" : "rgba(167,139,250,0.14)",
    border: "1px solid rgba(167,139,250,0.4)",
    color: "#a78bfa", cursor: working ? "default" : "pointer",
    fontFamily: "inherit",
  };
}
